import apiClient from './api-client';

// Interfaces matching the backend
export type ProductionConsentRecordStatus = 'ACTIVE' | 'EXPIRED' | 'UNKNOWN';

export interface ProductionConsentFilters {
    page?: number;
    pageSize?: number;
    collectionPointId?: number;
    clientId?: number;
    startDate?: Date;
    endDate?: Date;
    search?: string;
    sortBy?: string;
    consentRecordStatus?: ProductionConsentRecordStatus;
}

export interface ProductionConsentStatsData {
    total: number;
    active: number;
    expired: number;
    unknown: number;
}

export interface ProductionConsentStatsResponse {
    success: boolean;
    data: ProductionConsentStatsData;
}

export interface ProductionConsentData {
    ConsentHeaderId: number;
    DPId: number;
    CollectionPointId: number;
    CollectionPointIdentifier: string;
    ConsentTimestamp: Date;
    LanguageCode: string;
    CreatedDate: Date;
    IdentifierType: string;
    IdentifierValue: string;
    CollectionPointName: string;
    CollectionPointDescription: string;
    HasGeolocation: number;
    PrivacyPolicyVersion: string;
    YourRightsVersion: string;
    ContactDpoVersion: string;
}

// Comprehensive consent details interface matching the API response
export interface ProductionConsentDetails {
    details: {
        consentHeader: {
            consentHeaderId: number;
            consentTimestamp: string;
            consentSource: string;
            languageCode: string;
            createdDate: string;
        };
        dataPrincipal: {
            dpId: number;
            identifierType: string;
            identifierValue: string;
            vaultId?: number;
            isMinor?: boolean;
        };
        client: {
            clientId: number;
            clientName: string;
            clientDomain: string;
        };
    };
    collectionPoint: {
        id: number;
        departmentName: string;
        name: string;
        identifier: string;
        description: string;
        type: string;
        identifierDataElement: {
            name: string;
            type: string;
        };
        trackGeolocation: boolean;
        minorEnabled: boolean;
        parentIdentifier?: {
            name: string;
            type: string;
        };
    };
    geolocation: {
        latitude: number | null;
        longitude: number | null;
        accuracy: number | null;
        geolocationSource: string | null;
        geolocationTimestamp: string | null;
        countryCode: string | null;
        region: string | null;
        city: string | null;
        ipAddress: string | null;
    };
    privacyPolicy: {
        json: any;
        version: string | null;
        languageCode: string | null;
        effectiveDate: string | null;
        lastUpdated: string | null;
    };
    yourRights: {
        json: any;
        version: string | null;
        languageCode: string | null;
        effectiveDate: string | null;
        lastUpdated: string | null;
    };
    contactDpo: {
        json: any;
        version: string | null;
        languageCode: string | null;
        effectiveDate: string | null;
        lastUpdated: string | null;
    };
    dataElements: Array<{
        id: number;
        key: string;
        name: string;
        description: string;
        type: string[];
        isRequired: boolean;
        value: any;
        storageMode: string;
        isPersonalData: boolean;
    }>;
    purposes: Array<{
        id: number;
        key: string;
        name: string;
        description: string;
        consented: boolean;
        isRequired: boolean;
        lawfulGround: {
            id: number;
            code: string;
            label: string;
            description: string;
        };
        purposeCategory: {
            id: number;
            code: string;
            label: string;
            description: string;
        };
        retention: {
            type: {
                id: number;
                code: string;
                label: string;
            };
            timeBasedPeriod: number | null;
            timeBasedUnit: {
                id: number;
                code: string;
                label: string;
            } | null;
            startDateType: {
                id: number;
                code: string;
                label: string;
            };
            customStartDate: string | null;
            extensionAllowed: boolean;
            extensionReason: string | null;
            endConditions: {
                onPurposeFulfilled: boolean;
                onConsentWithdrawn: boolean;
                onRetentionExpired: boolean;
                onErasureRequested: boolean;
                onAccountClosure: boolean;
            };
            legalRetentionRequired: boolean;
            legalRetentionPeriod: number | null;
            expirationDate: string | null;
        };
        preferences: Array<{
            id: number;
            key: string;
            name: string;
            description: string;
            selectedOptions: Array<{
                id: number;
                key: string;
                label: string;
                value: string;
            }>;
            preferenceValue: string;
        }>;
    }>;

    // Collection Point Level Preferences
    collectionPointPreferences: Array<{
        id: number;
        key: string;
        name: string;
        description: string;
        type: string;
        isRequired: boolean;
        displayOrder: number;
        allowMultipleSelection: boolean;
        options: Array<{
            id: number;
            key: string;
            name: string;
            description: string;
            displayOrder: number;
            isDefaultSelected: boolean;
        }>;
        selectedOptions: Array<{
            id: number;
            key: string;
            label: string;
            value: string;
        }>;
        preferenceValue: string;
    }>;
}

export interface ProductionConsentDetailsResponse {
    success: boolean;
    data: ProductionConsentDetails;
}

export interface ProductionConsentListResult {
    data: ProductionConsentData[];
    pagination: {
        currentPage: number;
        pageSize: number;
        totalRecords: number;
        totalPages: number;
        hasNextPage: boolean;
        hasPrevPage: boolean;
    };
}

export interface ProductionConsentsQueryParams {
    filters?: Partial<ProductionConsentFilters>;
    pagination?: {
        page: number;
        pageSize: number;
    };
    sortBy?: string;
}

export interface ProductionConsentsResponse {
    success: boolean;
    data: ProductionConsentData[];
    pagination: {
        currentPage: number;
        pageSize: number;
        totalRecords: number;
        totalPages: number;
        hasNextPage: boolean;
        hasPrevPage: boolean;
    };
}

class ProductionConsentsService {
    /**
     * Get paginated production consents with filtering
     */
    async getProductionConsents(params: ProductionConsentsQueryParams): Promise<ProductionConsentsResponse> {
        const queryParams = new URLSearchParams();

        // Add pagination parameters
        if (params.pagination) {
            queryParams.append('page', params.pagination.page.toString());
            queryParams.append('pageSize', params.pagination.pageSize.toString());
        }

        // Add filter parameters
        if (params.filters) {
            if (params.filters.collectionPointId) {
                queryParams.append('collectionPointId', params.filters.collectionPointId.toString());
            }
            if (params.filters.startDate) {
                queryParams.append('startDate', params.filters.startDate.toISOString());
            }
            if (params.filters.endDate) {
                queryParams.append('endDate', params.filters.endDate.toISOString());
            }
            if (params.filters.search) {
                queryParams.append('search', params.filters.search);
            }
            if (params.filters.consentRecordStatus) {
                queryParams.append('consentRecordStatus', params.filters.consentRecordStatus);
            }
            if (params.filters.sortBy) {
                queryParams.append('sortBy', params.filters.sortBy);
            }
        }

        // Add sort parameter if provided at query level
        if (params.sortBy) {
            queryParams.append('sortBy', params.sortBy);
        }

        const response = await apiClient.get<ProductionConsentsResponse>(`/consents/production?${queryParams.toString()}`);
        return response;
    }

    /**
     * Get production consent by ID with comprehensive details
     */
    async getProductionConsentById(consentId: number): Promise<ProductionConsentDetailsResponse> {
        const response = await apiClient.get<ProductionConsentDetailsResponse>(`/consents/production/${consentId}`);
        return response;
    }

    async getProductionConsentStats(): Promise<ProductionConsentStatsResponse> {
        return apiClient.get<ProductionConsentStatsResponse>('/consents/production/stats');
    }
}

export const productionConsentsService = new ProductionConsentsService();
